import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { MessageService } from 'primeng/api';
import { AccountService } from 'src/app/service/account.service';
import { AuthService } from 'src/app/service/auth.service';
import { UserDataExportStatus } from 'src/app/model/user-data-export';
import { formatDateTime } from '../account-deletion/deletion-date.util';

/** Linha de status já resolvida: chave i18n + parâmetros de data formatados. */
export interface ExportStatusLine {
  key: string;
  params: Record<string, string>;
}

/**
 * Bloco de exportação dos dados da conta, reutilizado na aba "Dados da conta", na tela de
 * exclusão e na tela de carência. Concentra status, download e erros; as telas só o incluem.
 *
 * Contrato: ftcloud-api/docs/integracoes/frontend-angular/ANGULAR_EXPORTACAO_DE_DADOS.md.
 */
@Component({
  selector: 'app-data-export',
  templateUrl: './data-export.component.html',
  styleUrl: './data-export.component.css',
})
export class DataExportComponent implements OnInit {
  loadingStatus = true;
  exporting = false;
  status: UserDataExportStatus | null = null;
  statusLine: ExportStatusLine | null = null;

  private userId = '';

  constructor(
    private router: Router,
    private authService: AuthService,
    private accountService: AccountService,
    private messageService: MessageService,
    private translocoService: TranslocoService,
  ) {}

  ngOnInit(): void {
    this.userId = this.authService.getUserInformations() ?? '';
    this.loadStatus();
  }

  /** Desabilitado enquanto carrega o status e quando a janela de espera está fechada. */
  get buttonDisabled(): boolean {
    return this.loadingStatus || this.status?.canRequest === false;
  }

  /** Mapas e logs não entram na exportação: são baixados pela aba Arquivos. */
  goToArchives(): void {
    this.router.navigate(['/archives']);
  }

  /**
   * Baixa o ZIP. O nome do arquivo é dado aqui, e não lido do Content-Disposition, para não
   * depender de o CORS expor esse header.
   */
  exportData(): void {
    if (this.exporting || this.buttonDisabled || !this.userId) return;
    this.exporting = true;
    this.accountService.exportData(this.userId).subscribe({
      next: (blob) => {
        this.exporting = false;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ftpilot-dados-${new Date().toISOString().substring(0, 10)}.zip`;
        link.click();
        URL.revokeObjectURL(url);
        // É a partir daqui que a API passa a devolver regenerateUntil.
        this.loadStatus();
      },
      error: async (err: HttpErrorResponse) => {
        const body = await this.readErrorBody(err);
        this.exporting = false;
        if (err.status === 429 && body?.error === 'export_cooldown') {
          this.showError('user.dataExport.cooldown', { date: formatDateTime(body.nextAllowedAt) });
          this.loadStatus();
        } else if (err.status === 503 && body?.error === 'export_disabled') {
          this.showError('user.dataExport.disabled');
        } else if (err.status === 403 && body?.code === 'self_only') {
          this.showError('user.dataExport.selfOnly');
        } else {
          this.showError('user.dataExport.error');
        }
      },
    });
  }

  /**
   * Se o status falhar, o botão continua habilitado: o download devolve o erro real
   * (429, 403...) e a tela não fica travada por um aviso acessório.
   */
  private loadStatus(): void {
    if (!this.userId) {
      this.loadingStatus = false;
      return;
    }
    this.accountService.getExportStatus(this.userId).subscribe({
      next: (status) => {
        this.status = status;
        this.statusLine = this.buildStatusLine(status);
        this.loadingStatus = false;
      },
      error: () => {
        this.status = null;
        this.statusLine = null;
        this.loadingStatus = false;
      },
    });
  }

  /**
   * As datas vêm prontas da API (regenerateUntil e nextAllowedAt): o front nunca soma horas.
   * Sem linha para quem nunca exportou.
   */
  private buildStatusLine(status: UserDataExportStatus): ExportStatusLine | null {
    // Falha não conta prazo: o botão segue habilitado.
    if (status.lastExport?.status === 'failed') return { key: 'user.dataExport.lastFailed', params: {} };

    const generatedAt = formatDateTime(status.lastExport?.requestedAt);
    if (!status.canRequest) {
      const date = formatDateTime(status.nextAllowedAt);
      // Sem lastExport (ex.: export em andamento) só há a data de espera.
      return generatedAt
        ? { key: 'user.dataExport.generatedWaiting', params: { generatedAt, date } }
        : { key: 'user.dataExport.waitOnly', params: { date } };
    }
    if (status.lastExport) {
      return {
        key: 'user.dataExport.generatedWithin',
        params: { generatedAt, until: formatDateTime(status.regenerateUntil) },
      };
    }
    return null;
  }

  /** Com responseType 'blob', o corpo de erro também chega como Blob. */
  private async readErrorBody(err: HttpErrorResponse): Promise<any> {
    try {
      return err.error instanceof Blob ? JSON.parse(await err.error.text()) : err.error;
    } catch {
      return null;
    }
  }

  private showError(key: string, params?: Record<string, string>): void {
    this.messageService.add({ severity: 'error', summary: this.translocoService.translate(key, params) });
  }
}
